import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import DOMPurify from "dompurify";
import { api } from "../lib/config";
import { Reveal, SplitLines } from "../components/Motion";
import { ArrowLeft, Compass, Plane, MapPin } from "lucide-react";
import Seo from "../components/Seo";

export default function BlogPost() {
  const { slug } = useParams();
  const [b, setB] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  useEffect(() => { api.get(`/blogs/${slug}`).then((r) => setB(r.data)).catch(() => setB(false)); }, [slug]);

  if (b === null) return <div className="pt-32 text-center font-mono text-sm text-owt-muted">Loading...</div>;
  if (b === false) return <div className="pt-32 text-center"><Link to="/blog" className="underline">Post not found</Link></div>;

  const catLabel = b.category.replace(/-/g, " ");

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": b.title,
    "description": b.excerpt,
    "image": b.cover_image,
    "author": { "@type": "Organization", "name": b.author || "Once Way Travel" },
    "publisher": { "@type": "Organization", "name": "Once Way Travel" },
    "datePublished": b.created_at,
    "dateModified": b.updated_at || b.created_at,
    "articleSection": catLabel,
  };

  return (
    <article className="pt-24 md:pt-28 pb-24 relative overflow-hidden" data-testid={`blog-post-${b.slug}`}>
      <Seo
        title={b.title}
        description={b.excerpt}
        path={`/blog/${b.slug}`}
        image={b.cover_image}
        type="article"
        jsonLd={articleJsonLd}
      />
      {/* Decorative flight-path SVG */}
      <svg className="absolute inset-x-0 top-[600px] w-full h-[600px] opacity-[0.08] pointer-events-none" viewBox="0 0 1600 600" preserveAspectRatio="none">
        <path d="M-20 200 Q 400 60 800 240 T 1620 180" stroke="currentColor" strokeWidth="1.2" fill="none" strokeDasharray="6 8" className="text-owt-forest"/>
        <path d="M-20 400 Q 500 550 1000 420 T 1620 500" stroke="currentColor" strokeWidth="1.2" fill="none" strokeDasharray="4 8" className="text-owt-terracotta"/>
      </svg>

      {/* HERO */}
      <section ref={heroRef} className="relative h-[80svh] min-h-[520px] overflow-hidden">
        <motion.img
          style={{ y: heroY, scale: 1.05 }}
          src={b.cover_image}
          alt={b.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-owt-ink/85 via-owt-ink/25 to-transparent"/>

        <div className="relative z-10 h-full max-w-4xl mx-auto px-5 md:px-10 flex flex-col justify-end pb-12 md:pb-16 text-owt-surface">
          <Link to="/blog" className="font-mono text-[11px] tracking-[0.25em] uppercase inline-flex items-center gap-2 mb-6 hover:text-owt-terracotta w-fit"><ArrowLeft className="w-4 h-4"/> Back to Journal</Link>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase opacity-90 flex items-center gap-2 flex-wrap">
            <Compass className="w-3.5 h-3.5"/> {catLabel} <span className="opacity-50">·</span> {b.read_time} min read <span className="opacity-50">·</span> {b.author}
          </div>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight mt-4">
            <SplitLines text={b.title}/>
          </h1>
          {b.excerpt && (
            <p className="mt-5 max-w-2xl text-owt-surface/85 text-lg leading-relaxed">
              {b.excerpt}
            </p>
          )}
        </div>

        {/* Drifting plane */}
        <motion.div
          initial={{ opacity: 0, x: -60 }}
          animate={{ opacity: 0.8, x: 0 }}
          transition={{ delay: 0.9, duration: 1.4 }}
          className="hidden md:block absolute top-32 right-16 text-owt-surface/60 z-10"
        >
          <Plane className="w-8 h-8 -rotate-12" strokeWidth={1.1}/>
        </motion.div>
      </section>

      {/* Article body */}
      <div className="relative mx-auto max-w-3xl px-5 md:px-10 mt-14 md:mt-20">
        <Reveal>
          <div className="flex items-center gap-3 mb-8 font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted">
            <MapPin className="w-3.5 h-3.5 text-owt-terracotta"/>
            <span className="border-t border-dashed border-owt-border flex-1"/>
            <span>Field notes</span>
            <span className="border-t border-dashed border-owt-border flex-1"/>
          </div>
        </Reveal>

        {b.tags && b.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {b.tags.map((t) => (
              <span key={t} className="font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-1 border border-owt-border text-owt-forest">{t}</span>
            ))}
          </div>
        )}

        <div className="blog-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(b.content_html) }} />

        {/* Ending stamp */}
        <div className="mt-16 flex flex-col items-center gap-4 text-center">
          <div className="stamp bg-owt-surface">One way in · Est · 2026</div>
          <Link to="/blog" className="font-mono text-[11px] tracking-[0.25em] uppercase text-owt-forest hover:text-owt-terracotta">← More field notes</Link>
        </div>
      </div>
    </article>
  );
}
